import { Component, inject } from "@angular/core";
import { WidgetFilterKey } from "@app/dashboard/shared/models/WidgetFilterKey";
import { BaseWidget } from "./../BaseWidget";
import { WorkspaceCountService } from "@app/shared/services/workspace/workspace-count.service";
import { firstValueFrom } from "rxjs";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { IncidentService } from "@app/incident/services/incident.service";
import { PresetService } from "@app/preset/services/preset.service";
import { TabEventListenerType } from "@enums/TabEventListenerType.enum";
import { provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "module-detail-widget",
    templateUrl: "./module-detail-widget.component.html",
    styleUrls: ["./module-detail-widget.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("dashboard")]
})
export class ModuleDetailWidgetComponent extends BaseWidget {
    private _ds = inject(WorkspaceCountService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _settingsService = inject(SettingsDataService);
    private _incidentsService = inject(IncidentService);
    private _presetService = inject(PresetService);

    invalidConfig = false;

    count: number;

    itemName: string;
    isIncidentItem = false;
    presetName: string;
    usesAllIncidentsPreset = false;
    pageName: string;

    customTitle?: string;

    incidentModuleString: string = "Incidents";

    useIncidents: boolean = false;

    _loadTimeout: any;
    async load() {
        this.config.showLoader = true;
        this.useIncidents = (
            await firstValueFrom(this._settingsService.getSetting(ApplicationSettings.UseIncidents))
        ).BoolValue;
        if (this._loadTimeout) {
            clearTimeout(this._loadTimeout);
        }

        // Wait for 500ms before loading the widget (to prevent multiple calls to the API)
        this._loadTimeout = setTimeout(() => {
            this._loadTimeout = null;
            this.initDetails().then(() => {
                super.load();
            });
        }, 500);
    }

    openWorkspace() {
        this.onGlobalOpenClick();
    }

    protected onGlobalOpenClick() {
        if (this.invalidConfig) {
            return;
        }

        const configuredModule = this.getFilter(WidgetFilterKey.ModuleDetail.Module);
        const presetId = this.getFilter(WidgetFilterKey.ModuleDetail.WorkspacePreset);

        if (configuredModule == this.incidentModuleString && this.useIncidents) {
            this._tabService.generateTab(this._pages.IncidentOverview, null, "", {
                presetId: presetId,
            });
            this._tabService.listeners.triggerGlobalListener(
                TabEventListenerType.LoadPreset,
                this._pages.IncidentOverview,
                {
                    presetId: presetId,
                }
            );
        } else {
            this._tabService.generateTab(this._pages[this.pageName], null, "", {
                presetID: presetId,
            });
        }
    }

    private async initDetails() {
        const configuredModule = this.getFilter(WidgetFilterKey.ModuleDetail.Module);
        const configuredPreset = this.getFilter(WidgetFilterKey.ModuleDetail.WorkspacePreset);

        this.customTitle = this.getFilter(WidgetFilterKey.ModuleDetail.CustomTitle);

        if (!configuredModule) {
            this.invalidConfig = true;
            return;
        }

        if (this.useIncidents && configuredModule == this.incidentModuleString) {
            let preset = null;
            if (configuredPreset) {
                preset = await firstValueFrom(this._presetService.getPreset(configuredPreset));
            }

            this.isIncidentItem = true;
            this.presetName = preset ? preset.name : null;
            this.usesAllIncidentsPreset = !preset;
            this.pageName = this._pages.Incident;
            this.count = await firstValueFrom(this._incidentsService.getCount(configuredPreset));
            this.invalidConfig = false;
            return;
        } else {
            const moduleConfig = (await firstValueFrom(this._ds.getAvailableModules())).find(
                (x) => x.presetCode == configuredModule
            );

            this.itemName = moduleConfig.itemName;
            this.isIncidentItem = false;
            this.presetName = moduleConfig.presets.find((x) => x.ID == configuredPreset)?.Name;
            this.usesAllIncidentsPreset = false;
            this.pageName = moduleConfig.pageName;
        }
        const result = await firstValueFrom(this._ds.getCount(configuredModule, configuredPreset));

        this.count = result.count;
        this.invalidConfig = false;
    }
}
